import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCommentDots,
  faEnvelope,
  faPhone,
  faLink,
  faShieldAlt,
  faChartBar,
  faTimesCircle,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faExclamationCircle,
  faArrowLeft,
  faArrowRight,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";
import TryItOutAPI from "./services/TryItOutAPI";
import DOMPurify from "dompurify";
import PrivacyPolicy from "../legal/PrivacyPolicy";
import TermsAndConditions from "../legal/TermsAndConditions";
import Disclosures from "../legal/Disclosures";
import "./css/LandingPage-TryItOut.css";

// ── Constants ────────────────────────────────────────────────────────────────
const MAX_ANALYSES = 10;

const COUNTRY_CODES = [
  { code: "+1", flag: "🇺🇸", label: "US/CA" },
  { code: "+44", flag: "🇬🇧", label: "UK" },
  { code: "+61", flag: "🇦🇺", label: "AU" },
  { code: "+91", flag: "🇮🇳", label: "IN" },
  { code: "+49", flag: "🇩🇪", label: "DE" },
  { code: "+33", flag: "🇫🇷", label: "FR" },
  { code: "+81", flag: "🇯🇵", label: "JP" },
  { code: "+82", flag: "🇰🇷", label: "KR" },
  { code: "+86", flag: "🇨🇳", label: "CN" },
  { code: "+55", flag: "🇧🇷", label: "BR" },
  { code: "+52", flag: "🇲🇽", label: "MX" },
  { code: "+34", flag: "🇪🇸", label: "ES" },
  { code: "+39", flag: "🇮🇹", label: "IT" },
  { code: "+31", flag: "🇳🇱", label: "NL" },
  { code: "+46", flag: "🇸🇪", label: "SE" },
  { code: "+47", flag: "🇳🇴", label: "NO" },
  { code: "+64", flag: "🇳🇿", label: "NZ" },
  { code: "+65", flag: "🇸🇬", label: "SG" },
  { code: "+63", flag: "🇵🇭", label: "PH" },
  { code: "+27", flag: "🇿🇦", label: "ZA" },
  { code: "+234", flag: "🇳🇬", label: "NG" },
  { code: "+254", flag: "🇰🇪", label: "KE" },
];

const TABS = [
  { key: "text", label: "Text / SMS", icon: faCommentDots },
  { key: "email", label: "Email", icon: faEnvelope },
  { key: "phone", label: "Phone", icon: faPhone },
  { key: "url", label: "URL", icon: faLink },
];

// ── Helper: format phone number as user types ───────────────────────────────
function formatPhoneNumber(value, countryCode) {
  const digits = value.replace(/\D/g, "");
  if (countryCode === "+1") {
    // US/Canada: (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  // Other countries: group in blocks of 3-4
  if (digits.length <= 4) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 10)
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`.trim();
}

function stripPhone(value) {
  return value.replace(/\D/g, "");
}

// ── Helper: parse risk / threat level ────────────────────────────────────────
function getThreatInfo(result, tabKey) {
  if (!result) return null;

  let isScam = result.isScam;
  let riskLevel = (result.riskLevel || "").toLowerCase();

  // Determine banner class + title
  if (isScam === true || riskLevel === "high" || riskLevel === "danger") {
    const scamType = result.scamType ? result.scamType.trim() : null;
    return {
      cls: "threat-detected",
      title: scamType ? `Scam Detected: ${scamType}` : "Scam Detected",
      icon: faTimesCircle,
    };
  }
  if (riskLevel === "medium" || riskLevel === "warning") {
    return {
      cls: "warning",
      title: "Potential Threat Detected",
      icon: faExclamationTriangle,
    };
  }
  if (isScam === false || riskLevel === "low" || riskLevel === "safe") {
    return {
      cls: "safe",
      title: "No Threats Detected",
      icon: faCheckCircle,
    };
  }
  // Fallback
  return { cls: "safe", title: "Analysis Complete", icon: faCheckCircle };
}

// ── Helper: sanitize HTML for safe rendering (matches app pattern) ───────────
const ALLOWED_HTML_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "h3",
  "h4",
  "h5",
];

function sanitizeHtml(html) {
  if (!html) return "";
  const config = { ALLOWED_TAGS: ALLOWED_HTML_TAGS, ALLOWED_ATTR: [] };
  let cleaned = DOMPurify.sanitize(html, config);
  // Handle double-encoded HTML (e.g. &lt;p&gt; from escapeHtml)
  if (cleaned.includes("&lt;") && cleaned.includes("&gt;")) {
    const temp = document.createElement("div");
    temp.innerHTML = cleaned;
    cleaned = DOMPurify.sanitize(
      temp.textContent || temp.innerText || "",
      config,
    );
  }
  return cleaned;
}

function decodeAndSanitizeHtml(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html || "";
  return sanitizeHtml(txt.value);
}

// ── Helper: split text into individual sentences / bullet items ──────────────
function splitIntoItems(raw) {
  const text = DOMPurify.sanitize(String(raw), { ALLOWED_TAGS: [] });
  return text
    .split(/(?:\r?\n|(?<=\.)\s)/)
    .map((s) => s.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

// ── Helper: render bullet list from text or array ────────────────────────────
function BulletList({ text }) {
  if (!text) return null;
  let items;
  if (Array.isArray(text)) {
    items = text.flatMap((s) => splitIntoItems(s));
  } else {
    items = splitIntoItems(text);
  }
  if (items.length === 0) return null;
  return (
    <ul>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
function LandingPageTryItOut({ onManageAccountClick }) {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("text");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(MAX_ANALYSES);
  const [totalUsed, setTotalUsed] = useState(0);
  const [mobileSlide, setMobileSlide] = useState("input"); // "input" | "results"
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Per-tab form state
  const [textForm, setTextForm] = useState({
    countryCode: "+1",
    phone: "",
    message: "",
    context: "",
  });
  const [emailForm, setEmailForm] = useState({
    sender: "",
    subject: "",
    body: "",
  });
  const [phoneForm, setPhoneForm] = useState({
    countryCode: "+1",
    phone: "",
    context: "",
  });
  const [urlForm, setUrlForm] = useState({ url: "", context: "" });
  const [legalModal, setLegalModal] = useState(null); // "privacy" | "terms" | "disclosures" | null

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Fetch initial usage on mount ───────────────────────────────────────────
  useEffect(() => {
    TryItOutAPI.getUsage()
      .then((data) => {
        if (data) {
          const usage = data.data || data;
          setRemaining(usage.remaining ?? MAX_ANALYSES);
          setTotalUsed(usage.totalUsed ?? 0);
        }
      })
      .catch(() => {
        // first-time visitor — no record yet, defaults are fine
      });
  }, []);

  // ── Update usage from response metadata ────────────────────────────────────
  const updateUsageFromResponse = useCallback(
    (res) => {
      const meta = res?.tryItOut || res?.data?.tryItOut;
      if (meta) {
        setRemaining(meta.remaining ?? remaining);
        setTotalUsed(meta.totalUsed ?? totalUsed);
      }
    },
    [remaining, totalUsed],
  );

  // ── Clear results when switching tabs ──────────────────────────────────────
  const handleTabChange = (key) => {
    setActiveTab(key);
    setResult(null);
    setError(null);
    if (isMobile) setMobileSlide("input");
  };

  const clearActiveForm = () => {
    switch (activeTab) {
      case "text":
        setTextForm({
          ...textForm,
          phone: "",
          message: "",
          context: "",
        });
        break;
      case "email":
        setEmailForm({ sender: "", subject: "", body: "" });
        break;
      case "phone":
        setPhoneForm({
          ...phoneForm,
          phone: "",
          context: "",
        });
        break;
      case "url":
        setUrlForm({ url: "", context: "" });
        break;
      default:
        break;
    }

    setError(null);
    setResult(null);
    if (isMobile) setMobileSlide("input");
  };

  // ── Analyze handler ────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    if (remaining <= 0) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let res;
      switch (activeTab) {
        case "text":
          if (!textForm.message.trim()) {
            setError("Please enter a text message to analyze.");
            setLoading(false);
            return;
          }
          res = await TryItOutAPI.analyzeText(
            textForm.phone
              ? `${textForm.countryCode}${stripPhone(textForm.phone)}`
              : "",
            textForm.message,
            textForm.context,
          );
          break;

        case "email":
          if (!emailForm.body.trim()) {
            setError("Please enter email body content to analyze.");
            setLoading(false);
            return;
          }
          res = await TryItOutAPI.analyzeEmail(
            emailForm.sender,
            emailForm.subject,
            emailForm.body,
          );
          break;

        case "phone":
          if (!phoneForm.phone.trim()) {
            setError("Please enter a phone number to check.");
            setLoading(false);
            return;
          }
          res = await TryItOutAPI.checkPhoneNumber(
            `${phoneForm.countryCode}${stripPhone(phoneForm.phone)}`,
            phoneForm.context,
          );
          break;

        case "url":
          if (!urlForm.url.trim()) {
            setError("Please enter a URL to check.");
            setLoading(false);
            return;
          }
          res = await TryItOutAPI.checkURL(urlForm.url, urlForm.context);
          break;

        default:
          break;
      }

      if (res) {
        // Backend wraps analysis in `data`
        setResult(res.data || res);
        updateUsageFromResponse(res);
        if (isMobile) setMobileSlide("results");
      }
    } catch (err) {
      if (err.status === 402) {
        // Quota exhausted
        setRemaining(0);
        setError(
          err.message ||
            "You've used all 10 free analyses. Create a free account to continue, or upgrade to a paid plan for advanced features!",
        );
      } else if (err.status === 429) {
        setError("Too many requests — please wait a moment and try again.");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Usage bar helpers ──────────────────────────────────────────────────────
  const usagePercent = ((MAX_ANALYSES - remaining) / MAX_ANALYSES) * 100;
  const usageClass =
    remaining === 0 ? "exhausted" : remaining <= 3 ? "warning" : "";

  // ══════════════════════════════════════════════════════════════════════════
  // SUB-RENDERS
  // ══════════════════════════════════════════════════════════════════════════

  // ── Render input form for active tab ───────────────────────────────────────
  const renderInputForm = () => {
    switch (activeTab) {
      case "text":
        return (
          <>
            <div className="tryitout-field">
              <label>Phone Number</label>
              <div className="phone-input-group">
                <select
                  className="country-code-select"
                  value={textForm.countryCode}
                  onChange={(e) =>
                    setTextForm({ ...textForm, countryCode: e.target.value })
                  }
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.flag} {cc.code} {cc.label}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={textForm.phone}
                  onChange={(e) =>
                    setTextForm({
                      ...textForm,
                      phone: formatPhoneNumber(
                        e.target.value,
                        textForm.countryCode,
                      ),
                    })
                  }
                />
                {textForm.phone && (
                  <button
                    type="button"
                    className="phone-clear-btn"
                    onClick={() => setTextForm({ ...textForm, phone: "" })}
                    aria-label="Clear phone number"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="tryitout-field">
              <label>Text / SMS Message *</label>
              <textarea
                placeholder="Paste the suspicious text message here..."
                value={textForm.message}
                onChange={(e) =>
                  setTextForm({ ...textForm, message: e.target.value })
                }
                maxLength={1000}
              />
            </div>
            <div className="tryitout-field">
              <label>Context (optional)</label>
              <input
                type="text"
                placeholder="Any extra details help improve accuracy..."
                value={textForm.context}
                onChange={(e) =>
                  setTextForm({ ...textForm, context: e.target.value })
                }
              />
            </div>
          </>
        );

      case "email":
        return (
          <>
            <div className="tryitout-field">
              <label>Sender Email (optional)</label>
              <input
                type="email"
                placeholder="sender@example.com"
                value={emailForm.sender}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, sender: e.target.value })
                }
              />
            </div>
            <div className="tryitout-field">
              <label>Subject (optional)</label>
              <input
                type="text"
                placeholder="Email subject line..."
                value={emailForm.subject}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, subject: e.target.value })
                }
              />
            </div>
            <div className="tryitout-field">
              <label>Email Body *</label>
              <textarea
                placeholder="Paste the email content here..."
                value={emailForm.body}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, body: e.target.value })
                }
                maxLength={5000}
              />
            </div>
          </>
        );

      case "phone":
        return (
          <>
            <div className="tryitout-field">
              <label>Phone Number *</label>
              <div className="phone-input-group">
                <select
                  className="country-code-select"
                  value={phoneForm.countryCode}
                  onChange={(e) =>
                    setPhoneForm({ ...phoneForm, countryCode: e.target.value })
                  }
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.flag} {cc.code} {cc.label}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phoneForm.phone}
                  onChange={(e) =>
                    setPhoneForm({
                      ...phoneForm,
                      phone: formatPhoneNumber(
                        e.target.value,
                        phoneForm.countryCode,
                      ),
                    })
                  }
                />
                {phoneForm.phone && (
                  <button
                    type="button"
                    className="phone-clear-btn"
                    onClick={() => setPhoneForm({ ...phoneForm, phone: "" })}
                    aria-label="Clear phone number"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="tryitout-field">
              <label>Context (optional)</label>
              <textarea
                placeholder="Describe why you're checking this number..."
                value={phoneForm.context}
                onChange={(e) =>
                  setPhoneForm({ ...phoneForm, context: e.target.value })
                }
              />
            </div>
          </>
        );

      case "url":
        return (
          <>
            <div className="tryitout-field">
              <label>URL / Social Media Post Link *</label>
              <input
                type="url"
                placeholder="https://example.com/suspicious-link"
                value={urlForm.url}
                onChange={(e) =>
                  setUrlForm({ ...urlForm, url: e.target.value })
                }
              />
            </div>
            <div className="tryitout-field">
              <label>Context (optional)</label>
              <textarea
                placeholder="Any extra details help improve accuracy..."
                value={urlForm.context}
                onChange={(e) =>
                  setUrlForm({ ...urlForm, context: e.target.value })
                }
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // ── Render result panel content ────────────────────────────────────────────
  const renderResult = () => {
    // Error state
    if (error && !result) {
      return (
        <div className="tryitout-error">
          <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
          <p>{error}</p>
        </div>
      );
    }

    // No result yet
    if (!result) {
      return (
        <div className="tryitout-empty-state">
          <FontAwesomeIcon icon={faChartBar} className="empty-icon" />
          <h4>Ready to Analyze</h4>
          <p>
            Enter your content and click Analyze to see real-time results from
            our AI engine.
          </p>
        </div>
      );
    }

    const threat = getThreatInfo(result, activeTab);
    if (!threat) return null;

    return (
      <>
        {/* Threat banner */}
        <div className={`tryitout-threat-banner ${threat.cls}`}>
          <FontAwesomeIcon icon={threat.icon} className="threat-icon" />
          <h3 className="threat-title">{threat.title}</h3>
        </div>

        <div className="tryitout-result-details">
          {/* Suggestions / Recommendations — shown first */}
          {(result.suggestions || result.recommendations) && (
            <div className="result-section">
              <h5>What Should You Do?</h5>
              <BulletList text={result.suggestions || result.recommendations} />
            </div>
          )}

          {/* Summary */}
          {result.summary && (
            <div className="result-section">
              <h5>Explanation</h5>
              <div
                className="explanation-content"
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(result.summary),
                }}
              />
            </div>
          )}

          {/* Explanation — rendered as sanitized HTML, matching app pattern */}
          {result.explanation && (
            <div className="result-section">
              <h5>Explanation</h5>
              <div
                className="explanation-content"
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(result.explanation),
                }}
              />
            </div>
          )}

          {/* URL redirect detection */}
          {result.wasRedirect && (
            <div className="redirect-info">
              <strong>Redirect Detected:</strong> This link redirected through{" "}
              {result.wrapperPlatform
                ? `"${result.wrapperPlatform}"`
                : "another domain"}
              .
            </div>
          )}

          {/* Phone sources — hidden for phone tab */}
          {activeTab !== "phone" &&
            result.sources &&
            result.sources.length > 0 && (
              <div className="result-section">
                <h5>
                  Database Reports (
                  {result.totalReports || result.sources.length})
                </h5>
                {result.sources.map((src, i) => (
                  <div className="source-item" key={i}>
                    <span className="source-name">
                      {src.name || src.source || "Unknown"}
                    </span>
                    <span className="source-reports">
                      {src.reports ?? src.totalReports ?? "–"} reports
                    </span>
                  </div>
                ))}
              </div>
            )}

          {/* URL analyses from email */}
          {result.urlAnalyses && result.urlAnalyses.length > 0 && (
            <div className="result-section">
              <h5>URLs Found in Content</h5>
              <ul>
                {result.urlAnalyses.map((ua, i) => (
                  <li key={i}>
                    {ua.url} — {ua.isScam ? "⚠️ Suspicious" : "✓ Clean"}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <section className="tryitout-section" id="try-it-out">
      <div className="tryitout-inner">
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="tryitout-header">
          <h2>Try It Out — Free — No Account Required</h2>
        </div>

        {/* ── Descriptive text ────────────────────────────────────────────── */}

        <p className="tryitout-tagline">
          Paste a suspicious
          {/* 
          <span className="tagline-keyword">Text</span>, {"  "}
          <span className="tagline-keyword">Email</span>,{"  "}
          <span className="tagline-keyword">Phone Number</span>, or{" "}
          <span className="tagline-keyword">URL</span> <br />
          */}
          {/* ── Tabs ──────────────────────────────────────────────────────– */}
          <div className="tryitout-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`tryitout-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                <FontAwesomeIcon icon={tab.icon} className="tab-icon" />
                {tab.label}
              </button>
            ))}
          </div>
          and watch KinShield AI analyze it in seconds*
        </p>

        {/* ── Tabs ──────────────────────────────────────────────────────– */}
        {/* <div className="tryitout-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`tryitout-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} className="tab-icon" />
              {tab.label}
            </button>
          ))}
        </div> */}

        {/* ── Card: exhausted state ─────────────────────────────────────── */}
        {remaining <= 0 && !result ? (
          <div className="tryitout-card">
            <div className="tryitout-exhausted">
              <FontAwesomeIcon icon={faShieldAlt} className="exhausted-icon" />
              <h3>You've Used All 10 Free Analyses</h3>
              <p>
                Create a free account to keep scanning across all channels —
                text, email, phone, and URL.
              </p>
              <p className="exhausted-upgrade-hint">
                Sign up for a paid account to access many more advanced features
                including security training, phishing simulations, social media
                scam checks, image analysis, real-time alerts, detailed threat
                reports, and priority support.
              </p>
              <button
                className="signup-btn"
                onClick={() => navigate("/app?modal=create-account")}
              >
                <FontAwesomeIcon icon={faRocket} />
                Sign Up Free
              </button>
            </div>
          </div>
        ) : (
          /* ── Card: active state ──────────────────────────────────────── */
          <div
            className={`tryitout-card ${
              isMobile && mobileSlide === "results" ? "mobile-results" : ""
            }`}
          >
            {/* Input panel */}
            <div className="tryitout-input-panel">
              {renderInputForm()}

              <div className="tryitout-action-row">
                <button
                  className="tryitout-analyze-btn"
                  onClick={handleAnalyze}
                  disabled={loading || remaining <= 0}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="spinner" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faShieldAlt} />
                      Analyze
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="tryitout-clear-btn"
                  onClick={clearActiveForm}
                >
                  Clear
                </button>
              </div>

              {/* Mobile: link to jump to results */}
              {isMobile && result && mobileSlide === "input" && (
                <div className="tryitout-mobile-view-results">
                  <button onClick={() => setMobileSlide("results")}>
                    View Analysis Results{" "}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              )}

              {/* Legal agreement */}
              <div className="tryitout-legal-links">
                <p className="tryitout-legal-agreement">
                  By using this tool, you agree to our{" "}
                  <button onClick={() => setLegalModal("privacy")}>
                    Privacy Policy
                  </button>
                  ,{" "}
                  <button onClick={() => setLegalModal("terms")}>
                    Terms of Use
                  </button>
                  , and{" "}
                  <button onClick={() => setLegalModal("disclosures")}>
                    Disclosures
                  </button>
                  .
                </p>
              </div>
            </div>

            {/* Result panel */}
            <div className="tryitout-result-panel">
              {isMobile && mobileSlide === "results" && (
                <button
                  className="tryitout-mobile-back"
                  onClick={() => setMobileSlide("input")}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Input
                </button>
              )}

              {renderResult()}
            </div>
          </div>
        )}

        {/* ── Usage bar ─────────────────────────────────────────────────── */}
        <div className="tryitout-usage-bar">
          <span className="usage-label">Free analyses:</span>
          <div className="usage-track">
            <div
              className={`usage-fill ${usageClass}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <span className={`usage-count ${usageClass}`}>
            {remaining} / {MAX_ANALYSES} remaining
          </span>
        </div>

        {/* ── Legal modal ───────────────────────────────────────────────– */}
        {legalModal && (
          <div
            className="tryitout-legal-overlay"
            onClick={() => setLegalModal(null)}
          >
            <div
              className="tryitout-legal-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="tryitout-legal-back"
                onClick={() => setLegalModal(null)}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
              <div className="tryitout-legal-body">
                {legalModal === "privacy" && <PrivacyPolicy />}
                {legalModal === "terms" && <TermsAndConditions />}
                {legalModal === "disclosures" && <Disclosures />}
              </div>
            </div>
          </div>
        )}

        {/* ── CTA nudge when running low ────────────────────────────────– */}
        {remaining > 0 && remaining <= 3 && (
          <div className="tryitout-cta-nudge">
            <p>
              You have <strong>{remaining}</strong> free{" "}
              {remaining === 1 ? "analysis" : "analyses"} left. Sign up for a
              paid account to get more usage and access to many more advanced
              features including security training, phishing simulations, social
              media scam checks, image analysis, real-time alerts, detailed
              threat reports, and priority support.
            </p>
            <button
              className="cta-btn"
              onClick={() => navigate("/app?modal=create-account")}
            >
              <FontAwesomeIcon icon={faRocket} />
              Create an Account
            </button>
          </div>
        )}

        {/* ── Descriptive text - upgrade hint ────────────────────────────── */}
        {remaining > 3 && (
          <p className="tryitout-upgrade-hint">
            * Want more?{" "}
            <span
              className="tryitout-upgrade-link"
              onClick={() => navigate("/app?modal=create-account")}
            >
              Create an account
            </span>{" "}
            for more scans, phishing simulations, image analysis, real-time
            alerts, forwarding emails, texts, social media posts, <br /> Digital
            Personal Guard, trainings &amp; priority support.
          </p>
        )}
      </div>
    </section>
  );
}

export default LandingPageTryItOut;
